import { isDevMode, inject } from '@angular/core';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateStore,
} from '@ngx-translate/core';
import { isI18nLoading } from 'src/app/app.translation';

/**
 * Global Missing Translation Handler
 *
 * Prevents raw technical keys (e.g. "linearReport.setupTitle" or "⚑ linearReport.title")
 * from flashing on screen while translation JSON files are being downloaded or when moving
 * across feature modules.
 */
export class AppMissingTranslationHandler implements MissingTranslationHandler {
  private store = inject(TranslateStore);

  handle(params: MissingTranslationHandlerParams): any {
    const key = params.key;
    const translateService = params.translateService;
    const enTranslation = this.store.getTranslations('en') || {};
    const coreTranslation =
      this.store.getTranslations(params.translateService.getCurrentLang()) ||
      {};

    // 0. Detect already-translated text or raw values
    const isAlreadyTranslated = /[^\x00-\x7F]/.test(key);
    const isRawLabel = key.includes(' ');

    if (isAlreadyTranslated || isRawLabel) {
      return key;
    }

    // 1. Fallback logic: If it's not English, try to get the English version from the same path
    if (params.translateService.getCurrentLang() !== 'en') {
      const value = key
        .split('.')
        .reduce<any>((obj, i) => obj?.[i], enTranslation);
      if (value && typeof value === 'string') return value;
    }

    // 2. Global Shared Fallback
    const keyParts = key.split('.');
    const rawSuffix = keyParts[keyParts.length - 1];
    const normalizedSuffix = rawSuffix.replace(/[\s_]/g, '').toUpperCase();

    const findInShared = (obj: any): string | null => {
      if (!obj?.['SHARED']) return null;
      const shared = obj['SHARED'];

      // 1. Check direct match in SHARED root
      if (
        shared[normalizedSuffix] &&
        typeof shared[normalizedSuffix] === 'string'
      ) {
        return shared[normalizedSuffix];
      }

      // 2. Deep search in common namespaces
      const namespaces = [
        'LABELS',
        'COMMON',
        'STATUS',
        'BUTTONS',
        'FORMS',
        'TABLE',
      ];
      for (const ns of namespaces) {
        if (shared[ns]) {
          const subMatch = shared[ns][normalizedSuffix];
          if (subMatch && typeof subMatch === 'string') return subMatch;

          // Try fuzzy match
          const match = Object.keys(shared[ns]).find(
            k => k.replace(/_/g, '').toUpperCase() === normalizedSuffix
          );
          if (match && typeof shared[ns][match] === 'string')
            return shared[ns][match] as string;
        }
      }

      // 3. Last resort: Value match
      if (shared['LABELS']) {
        const valueMatch = Object.entries(shared['LABELS']).find(
          ([k, v]) =>
            typeof v === 'string' && v.toLowerCase() === key.toLowerCase()
        );
        if (valueMatch && typeof valueMatch[1] === 'string')
          return valueMatch[1];
      }

      return null;
    };

    const sharedValue =
      findInShared(coreTranslation) || findInShared(enTranslation);
    if (sharedValue) return sharedValue;

    // 3. While translation files are actively loading in the background, return empty string
    // so no raw dot-notation keys flash in templates before the translations populate.
    if (isI18nLoading()) {
      return '';
    }

    // 4. If key is a structured token (e.g. "linearReport.title"), avoid rendering broken keys
    if (key.includes('.')) {
      if (isDevMode()) {
        console.warn(
          `[I18n] Pending/Missing key: "${key}" in language: "${translateService.getCurrentLang()}"`
        );
      }
      return '';
    }

    return key;
  }
}
